import React, { useCallback, useMemo } from 'react';
import { useDescope, useSession } from '@descope/react-sdk';
import { ApiClientsProvider, type ApiClientsProviderProps } from 'design-system/my-lib';

type Props = Omit<ApiClientsProviderProps, 'auth'>;

/**
 * Bridges Descope's session into the readiness signal `ApiClientsProvider` gates every
 * client behind, so the clients exist only while a Descope session does, and hands the
 * clients Descope's own session renewal for a request answered 401.
 */
export const DescopeApiClientsProvider: React.FC<Props> = ({ children, ...rest }) => {
  const { isSessionLoading, isAuthenticated, claims } = useSession();
  const { refresh } = useDescope();

  // `refresh()` with no argument renews from the refresh token the SDK persisted, and the
  // SDK itself publishes the outcome: a renewed session updates `useSession`, an ended one
  // clears its tokens and flips `isAuthenticated`, which `SessionEndGuard` turns into sign-out.
  const refreshSession = useCallback(async () => {
    const response = await refresh();
    return !!response?.ok;
  }, [refresh]);

  const auth = useMemo(
    () => ({
      isAuthenticated: !isSessionLoading && isAuthenticated,
      accountKey: isAuthenticated ? ((claims?.sub as string | undefined) ?? 'descope') : '',
      refreshSession,
    }),
    [isSessionLoading, isAuthenticated, claims, refreshSession]
  );

  return (
    <ApiClientsProvider {...rest} auth={auth}>
      {children}
    </ApiClientsProvider>
  );
};
